import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import {
  ADDON_KEYS,
  ADDON_META,
  type AddonKey,
  type AddonSelection,
  NO_ADDONS,
  type PlanPricing,
  addonLines,
  addonPayload,
  formatMinutes,
  formatMoney,
  formatRate,
  includedSelection,
  monthlyTotalCents,
  planHue,
} from '../../shared/plan';
import {
  Button,
  Callout,
  Checkbox,
  FeatureCheck,
  FeatureChecks,
  Field,
  IconArrowRight,
  IconCalendar,
  IconCard,
  IconCheckCircle,
  IconClock,
  IconDollar,
  IconLock,
  IconFact,
  IconFacts,
  Input,
  NextSteps,
  PlanCard,
  PlanChooser,
  type PlanOption,
  PlanName,
  PublicFooter,
  PublicPage,
  PublicPrimary,
  PublicShell,
  PublicTitle,
  Reason,
  Receipt,
  ReceiptDue,
  ReceiptGroup,
  type ReceiptLine,
  ReceiptLines,
  ReceiptTotal,
  Reveal,
  SUPPORT_EMAIL,
  Segmented,
  Select,
  Skeleton,
  StatusLabel,
  StepCard,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Tag,
} from '../../ui/v2';
import { apiEndpoints, apiPublic } from '../../utils/apiService';
import { type Plan } from '../Billing/model';
import {
  type BusinessForm,
  COUNTRIES,
  COUNTRY_CURRENCY,
  COUNTRY_LABEL,
  type Country,
  EMPTY_FORM,
  type FormErrors,
  type GroupLocation,
  LOCATION_ADDON_FIELD,
  LOCATION_COUNTS,
  type Offer,
  TC_VERSION,
  TERMS_SUMMARY,
  billingStartsLater,
  formatOfferDate,
  locationAddonCents,
  normalizedLocations,
  offerAddons,
  offerShape,
  offerState,
  optionFromOffer,
  optionFromPlan,
  validate,
} from './activationModel';

/**
 * Activation V2 — the public checkout page (`/activate[/:token]`).
 *
 * One long page, not a wizard: numbered `StepCard`s (Plan → Business &
 * contact → Agreement) on the canvas, and a sticky Receipt that owns the
 * one primary ("Continue to secure payment"). A single prepared offer
 * renders as a calm `PlanCard` (tile, checklist, add-on checks); a choice
 * of plans keeps the same PlanChooser as Billing › Plans.
 *
 * Offer shapes (activationModel.ts): standard catalog · single custom offer
 * · a menu of offers · one plan across existing locations (add-on matrix).
 * Dead ends (paid, expired, withdrawn, invalid link, load error) use the
 * narrow shell with one action each.
 */
const ActivationPage: React.FC = () => (
  <PublicPage>
    <Inner />
  </PublicPage>
);

export default ActivationPage;

const LOAD_ERROR = 'Check your connection and try again.';

async function readDetail(res: Response | null | undefined, fallback: string) {
  const body = res ? await res.json().catch(() => null) : null;
  return (body && typeof body.detail === 'string' && body.detail) || fallback;
}

const Inner: React.FC = () => {
  const { token } = useParams<{ token?: string }>();
  const [params, setParams] = useSearchParams();
  const country = (params.get('country') || 'US').toUpperCase();
  const storeUuid = params.get('store');
  const intake = params.get('intake');

  // GET /api/public/activation/offers/{token}/  (404 → null)
  const offerQ = useQuery<Offer | null>({
    queryKey: ['public-activation', 'offer', token],
    queryFn: async () => {
      const res = await apiPublic.get(
        apiEndpoints.getPublicActivationOffer(token!),
      );
      if (res && res.status === 404) return null;
      if (!res || !res.ok) throw new Error(await readDetail(res, LOAD_ERROR));
      return res.json();
    },
    enabled: !!token,
    staleTime: 30_000,
  });
  // GET /api/public/activation/plans/?country=&store_uuid=
  const plansQ = useQuery<Plan[]>({
    queryKey: ['public-activation', 'plans', country, storeUuid],
    queryFn: async () => {
      const res = await apiPublic.get(
        apiEndpoints.getPublicActivationPlans(country, storeUuid || undefined),
      );
      if (!res || !res.ok) throw new Error(await readDetail(res, LOAD_ERROR));
      return res.json();
    },
    enabled: !token,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  const setCountry = (c: Country) => {
    const next = new URLSearchParams(params);
    next.set('country', c);
    setParams(next, { replace: true });
  };

  const q = token ? offerQ : plansQ;
  if (q.status === 'error') {
    return (
      <PublicShell width="narrow" aside={<SecureNote label="Secure link" />}>
        <PageMeta
          title="AskArthur | Activate"
          description="Activate AskArthur"
        />
        <PublicTitle lede="Nothing was charged. This is usually a connection hiccup.">
          We couldn’t load this page.
        </PublicTitle>
        <Callout tone="danger" className="mt-5">
          {(q.error as Error)?.message || LOAD_ERROR}
        </Callout>
        <div className="mt-5">
          <PublicPrimary onClick={() => void q.refetch()}>
            Try again
          </PublicPrimary>
        </div>
      </PublicShell>
    );
  }
  if (q.status === 'pending') return <LoadingShell />;

  if (token) {
    const offer = offerQ.data;
    if (!offer) return <DeadEnd kind="invalid" />;
    const state = offerState(offer);
    if (state !== 'open')
      return <DeadEnd kind={state} offer={offer} token={token} />;
    return (
      <Checkout
        offer={offer}
        plans={[]}
        country={offer.currency === 'CAD' ? 'CA' : 'US'}
        storeUuid={null}
        intake={null}
      />
    );
  }
  return (
    <Checkout
      offer={null}
      plans={plansQ.data ?? []}
      country={country}
      onCountry={setCountry}
      preselect={params.get('plan') || 'growth'}
      storeUuid={storeUuid}
      intake={intake}
    />
  );
};

// ---------------------------------------------------------------------------
// The checkout page
// ---------------------------------------------------------------------------

const Checkout: React.FC<{
  offer: Offer | null;
  plans: Plan[];
  country: string;
  onCountry?: (c: Country) => void;
  preselect?: string;
  storeUuid: string | null;
  intake: string | null;
}> = ({ offer, plans, country, onCountry, preselect, storeUuid, intake }) => {
  const multi = !!offer && offerShape(offer) === 'multi';
  const offers: Offer[] = useMemo(
    () => (offer ? (multi ? (offer.offers ?? []) : [offer]) : []),
    [offer, multi],
  );

  const options: PlanOption[] = useMemo(
    () => (offer ? offers.map(optionFromOffer) : plans.map(optionFromPlan)),
    [offer, offers, plans],
  );

  const pickDefault = (opts: PlanOption[]) => {
    if (opts.length === 0) return null;
    const wanted = preselect?.toLowerCase();
    return (
      opts.find((o) => o.code.toLowerCase() === wanted)?.code ?? opts[0].code
    );
  };
  const [selected, setSelected] = useState<string | null>(() =>
    pickDefault(options),
  );
  const offerFor = (code: string | null) =>
    offer ? (offers.find((o) => o.token === code) ?? null) : null;
  const selectedOffer = offerFor(selected);
  const option = options.find((o) => o.code === selected) ?? null;
  const groupScoped = !!selectedOffer && offerShape(selectedOffer) === 'group';

  const seedAddons = (code: string | null): AddonSelection => {
    const so = offerFor(code);
    if (so) return offerAddons(so);
    const o = options.find((x) => x.code === code);
    return o ? includedSelection(o.pricing) : NO_ADDONS;
  };
  const [addons, setAddons] = useState<AddonSelection>(() =>
    seedAddons(selected),
  );
  const [locations, setLocations] = useState<GroupLocation[]>(() =>
    selectedOffer ? normalizedLocations(selectedOffer) : [],
  );

  const choose = (code: string) => {
    setSelected(code);
    setAddons(seedAddons(code));
    const so = offerFor(code);
    setLocations(so ? normalizedLocations(so) : []);
  };

  // A country switch reloads plans in another currency; keep the same plan
  // code when it exists there.
  useEffect(() => {
    if (offer) return;
    if (selected && options.some((o) => o.code === selected)) return;
    const next = pickDefault(options);
    setSelected(next);
    const o = options.find((x) => x.code === next);
    setAddons(o ? includedSelection(o.pricing) : NO_ADDONS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const toggleLocation = (storeId: number, key: AddonKey, on: boolean) =>
    setLocations((ls) =>
      ls.map((l) =>
        l.store_id === storeId ? { ...l, [LOCATION_ADDON_FIELD[key]]: on } : l,
      ),
    );

  const [form, setForm] = useState<BusinessForm>(() =>
    offer
      ? {
          ...EMPTY_FORM,
          business_name: offer.business_name ?? '',
          signer_name: offer.signer_name ?? '',
          signer_email: offer.signer_email ?? '',
          signer_phone: offer.signer_phone ?? '',
        }
      : EMPTY_FORM,
  );
  const patch =
    (k: keyof BusinessForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const firstErrorRef = useRef<HTMLElement>(null);

  const currency = (
    selectedOffer?.currency ??
    option?.pricing.currency ??
    COUNTRY_CURRENCY[country] ??
    'USD'
  ).toUpperCase();
  const pricing: PlanPricing | null = option?.pricing ?? null;
  const addonMonthly = pricing
    ? groupScoped
      ? locations.reduce((s, l) => s + locationAddonCents(pricing, l), 0)
      : monthlyTotalCents(pricing, addons) - pricing.baseCents
    : 0;
  const monthly = (pricing?.baseCents ?? 0) + addonMonthly;
  const setup = pricing?.setupFeeCents ?? 0;
  const feeOnly = selectedOffer?.checkout_mode === 'activation_fee_only';
  const startIso = selectedOffer?.billing_start_date ?? null;
  // The server starts the plan on a trial until a start date ≥ 48h away.
  const startsLater = billingStartsLater(startIso);
  const dueToday = feeOnly || startsLater ? setup : setup + monthly;
  const startDate = startIso ? formatOfferDate(startIso) : 'today';
  useEffect(() => {
    setAgreed(false);
  }, [selected, currency, monthly, setup, startIso, feeOnly, locations]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate(form, agreed, !!option, !groupScoped);
    setErrors(errs);
    setServerError(null);
    if (Object.keys(errs).length > 0) {
      window.setTimeout(
        () =>
          firstErrorRef.current?.scrollIntoView?.({
            behavior: 'smooth',
            block: 'center',
          }),
        0,
      );
      return;
    }
    if (!option || !pricing) return;
    setBusy(true);
    const payload = {
      signer_name: form.signer_name.trim(),
      signer_email: form.signer_email.trim(),
      signer_phone: form.signer_phone.trim(),
      business_name: form.business_name.trim(),
      tc_version: TC_VERSION,
      business_agreed: agreed,
      ...addonPayload(pricing, addons),
      store_name: form.store_name.trim(),
      signer_title: form.signer_title.trim(),
      num_locations: form.num_locations,
      ...(groupScoped
        ? {
            locations: locations.map((l) => ({
              store_id: l.store_id,
              multilingual_addon_active: l.multilingual_addon_active,
              sms_ordering_addon_active: l.sms_ordering_addon_active,
              opentable_resy_addon_active: l.opentable_resy_addon_active,
            })),
          }
        : {}),
      ...(offer
        ? {}
        : {
            plan_code: option.code,
            country,
            store_uuid: storeUuid,
            onboarding_intake_id: intake,
          }),
    };
    try {
      const url = selectedOffer
        ? apiEndpoints.acceptPublicActivationOffer(selectedOffer.token)
        : apiEndpoints.acceptPublicStandardActivation();
      const res = await apiPublic.post(url, payload);
      const data = res ? await res.json().catch(() => ({})) : {};
      if (!res || !res.ok)
        throw new Error(
          data?.detail ||
            'Stripe could not start the checkout. Nothing was charged — try again in a moment.',
        );
      window.location.href = data?.checkout_url || '/billing';
    } catch (err) {
      setServerError((err as Error).message);
      setBusy(false);
    }
  };

  const errorCount = Object.keys(errors).length;
  const formError =
    errors.business_name ||
    errors.store_name ||
    errors.signer_name ||
    errors.signer_email ||
    errors.signer_phone;

  // The group well holds the recurring lines; setup (one-time) lives in
  // the "Due today" note so the monthly numbers add up on sight.
  const lines: ReceiptLine[] = [];
  if (option && pricing) {
    lines.push({
      key: 'base',
      label: `${option.name} plan`,
      sub: groupScoped
        ? `shared by ${locations.length} locations`
        : `${formatMinutes(pricing.includedMinutes)} included`,
      value: `${formatMoney(pricing.baseCents, currency)}/mo`,
    });
    if (groupScoped) {
      locations.forEach((l) => {
        const priced = ADDON_KEYS.filter(
          (k) => l[LOCATION_ADDON_FIELD[k]] && !pricing.addons[k].included,
        );
        if (priced.length === 0) return;
        lines.push({
          key: `loc-${l.store_id}`,
          label: l.store_name,
          sub: priced.map((k) => ADDON_META[k].label).join(', '),
          value: `${formatMoney(locationAddonCents(pricing, l), currency)}/mo`,
        });
      });
    } else {
      addonLines(pricing, addons)
        .filter((a) => a.active)
        .forEach((a) =>
          lines.push({
            key: a.key,
            label: a.label,
            value: a.included
              ? 'Included'
              : `${formatMoney(a.cents, currency)}/mo`,
            muted: a.included,
          }),
        );
    }
  }

  // A single prepared offer renders as one calm tile instead of a chooser.
  // Add-ons that arrive switched on (or included) sit in the checklist;
  // the rest wait behind "See other add-ons". The split follows the seed,
  // not the live selection, so rows don't jump while being toggled.
  const singleOffer = !!offer && !multi;
  const seeded = seedAddons(selected);
  const shownAddons =
    pricing && !groupScoped
      ? ADDON_KEYS.filter((k) => pricing.addons[k].included || seeded[k])
      : [];
  const otherAddons =
    pricing && !groupScoped
      ? ADDON_KEYS.filter((k) => !pricing.addons[k].included && !seeded[k])
      : [];
  const addonCheck = (k: AddonKey, withSub?: boolean) => {
    const a = pricing!.addons[k];
    return (
      <FeatureCheck
        key={k}
        label={ADDON_META[k].label}
        sub={withSub ? ADDON_META[k].description : undefined}
        note={
          a.included ? 'Included' : `+${formatRate(a.cents, currency, 'mo')}`
        }
        checked={a.included || addons[k]}
        onChange={
          a.included ? undefined : (on) => setAddons({ ...addons, [k]: on })
        }
        disabledReason={a.included ? undefined : option?.addonsLocked}
      />
    );
  };

  const businessName = offer?.business_name || 'your business';
  const title = !offer
    ? 'Start answering every call'
    : multi
      ? `${businessName}, pick your plan`
      : groupScoped
        ? `One plan for ${businessName}`
        : `${businessName}, your plan is ready`;

  return (
    <PublicShell
      width="checkout"
      aside={
        <span className="inline-flex items-center gap-3">
          {onCountry && (COUNTRIES as string[]).includes(country) ? (
            <Segmented
              aria-label="Country"
              items={COUNTRIES.map((c) => ({ key: c, label: c }))}
              value={country as Country}
              onChange={onCountry}
            />
          ) : (
            <Tag>{currency}</Tag>
          )}
          <span className="hidden sm:inline-flex">
            <SecureNote label="Secure checkout" />
          </span>
        </span>
      }
      footer={<PublicFooter />}
    >
      <PageMeta
        title="AskArthur | Activate"
        description="Choose a plan and start answering every call"
      />
      <form
        onSubmit={onSubmit}
        noValidate
        className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8"
      >
        <div className="flex min-w-0 flex-col gap-5">
          <PublicTitle
            className="mb-1"
            eyebrow={
              offer
                ? undefined
                : `Activate AskArthur · ${COUNTRY_LABEL[country] ?? currency}`
            }
            lede={
              offer
                ? 'Review the plan, confirm who is signing and continue to payment.'
                : 'Pick a plan, tell us who is signing and continue to payment. Onboarding starts right after, and Arthur is usually live within a few days.'
            }
          >
            {title}
          </PublicTitle>

          <StepCard
            n={1}
            title={
              multi
                ? 'Choose your offer'
                : offer
                  ? 'Your plan'
                  : 'Choose a plan'
            }
            description={
              groupScoped
                ? 'Minutes are pooled across every location below. Add-ons are switched on per location.'
                : multi
                  ? 'Each was prepared for you; the rest of the page updates with your choice.'
                  : offer
                    ? undefined
                    : 'Plans differ by included minutes and which add-ons are included.'
            }
            error={errors.plan}
            ref={errors.plan ? firstErrorRef : undefined}
          >
            {options.length === 0 ? (
              <Reason>
                No plans are available for this country. Write to{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
                  {SUPPORT_EMAIL}
                </a>{' '}
                and we’ll set one up.
              </Reason>
            ) : singleOffer && option && pricing ? (
              <PlanCard
                name={option.name}
                hue={option.hue ?? planHue(option.code)}
                tag={
                  option.tag && (
                    <Tag tone={option.tag.tone}>{option.tag.label}</Tag>
                  )
                }
                meta={
                  groupScoped
                    ? `${formatMinutes(pricing.includedMinutes)} shared across ${locations.length} locations · ${formatRate(pricing.overageCentsPerMinute, currency, 'min')} after`
                    : `${formatMinutes(pricing.includedMinutes)} included · ${formatRate(pricing.overageCentsPerMinute, currency, 'min')} after`
                }
                price={formatMoney(pricing.baseCents, currency)}
                priceSub="/mo"
              >
                {(option.features?.length ?? 0) > 0 ||
                shownAddons.length > 0 ||
                otherAddons.length > 0 ? (
                  <>
                    {((option.features?.length ?? 0) > 0 ||
                      shownAddons.length > 0) && (
                      <FeatureChecks>
                        {option.features?.map((f) => (
                          <FeatureCheck key={f} label={f} />
                        ))}
                        {shownAddons.map((k) => addonCheck(k))}
                      </FeatureChecks>
                    )}
                    {otherAddons.length > 0 && (
                      <Reveal
                        label="See other add-ons"
                        className={
                          (option.features?.length ?? 0) > 0 ||
                          shownAddons.length > 0
                            ? 'mt-3'
                            : undefined
                        }
                      >
                        <FeatureChecks>
                          {otherAddons.map((k) => addonCheck(k, true))}
                        </FeatureChecks>
                      </Reveal>
                    )}
                  </>
                ) : undefined}
              </PlanCard>
            ) : (
              <PlanChooser
                options={options}
                value={selected}
                onChange={choose}
                addons={addons}
                onAddonsChange={setAddons}
                showAddons={!groupScoped}
                aria-label={multi ? 'Offers' : 'Plans'}
              />
            )}
            {groupScoped && pricing && (
              <LocationMatrix
                locations={locations}
                pricing={pricing}
                currency={currency}
                onToggle={toggleLocation}
              />
            )}
            {pricing && pricing.perOrderCents > 0 ? (
              <Reason className="mt-3">
                Confirmed orders are{' '}
                {formatRate(pricing.perOrderCents, currency, 'order')}.
              </Reason>
            ) : null}
          </StepCard>

          <StepCard
            n={2}
            title="Business & contact"
            ref={!errors.plan && formError ? firstErrorRef : undefined}
          >
            <div className="grid grid-cols-2 gap-x-4 gap-y-4">
              <Field
                label="Legal business name"
                hint={groupScoped ? undefined : 'As it appears on invoices.'}
                error={errors.business_name}
                className={
                  groupScoped ? 'col-span-2' : 'col-span-2 sm:col-span-1'
                }
              >
                {(id, by) => (
                  <Input
                    id={id}
                    aria-describedby={by}
                    value={form.business_name}
                    onChange={patch('business_name')}
                    invalid={!!errors.business_name}
                    autoComplete="organization"
                  />
                )}
              </Field>
              {!groupScoped && (
                <Field
                  label="Store name"
                  hint="What callers hear Arthur say."
                  error={errors.store_name}
                  className="col-span-2 sm:col-span-1"
                >
                  {(id, by) => (
                    <Input
                      id={id}
                      aria-describedby={by}
                      value={form.store_name}
                      onChange={patch('store_name')}
                      invalid={!!errors.store_name}
                    />
                  )}
                </Field>
              )}
              {!groupScoped && (
                <Field
                  label="Locations"
                  hint="How many locations will use AskArthur."
                  className="col-span-2 sm:col-span-1"
                >
                  {(id, by) => (
                    <Select
                      id={id}
                      aria-describedby={by}
                      value={form.num_locations}
                      onChange={patch('num_locations')}
                    >
                      {LOCATION_COUNTS.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              )}
              {groupScoped && (
                <p className="col-span-2 text-v2-13 text-v2-fg-2">
                  <span className="v2-num">{locations.length}</span> locations
                  on one agreement:{' '}
                  {locations.map((l) => l.store_name).join(', ')}.
                </p>
              )}
            </div>
            <div className="mt-5 border-t border-v2-line/70 pt-4">
              <h3 className="text-v2-13 font-semibold text-v2-fg">
                Authorized signer
              </h3>
              <p className="mt-0.5 text-v2-12 text-v2-fg-3">
                Must be authorized to bind the business.
              </p>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4">
                <Field
                  label="Full name"
                  error={errors.signer_name}
                  className="col-span-2 sm:col-span-1"
                >
                  {(id, by) => (
                    <Input
                      id={id}
                      aria-describedby={by}
                      value={form.signer_name}
                      onChange={patch('signer_name')}
                      invalid={!!errors.signer_name}
                      autoComplete="name"
                    />
                  )}
                </Field>
                <Field
                  label="Title"
                  optional
                  className="col-span-2 sm:col-span-1"
                >
                  {(id, by) => (
                    <Input
                      id={id}
                      aria-describedby={by}
                      value={form.signer_title}
                      onChange={patch('signer_title')}
                      placeholder="Owner, General manager…"
                      autoComplete="organization-title"
                    />
                  )}
                </Field>
                <Field
                  label="Email"
                  error={errors.signer_email}
                  className="col-span-2 sm:col-span-1"
                >
                  {(id, by) => (
                    <Input
                      id={id}
                      aria-describedby={by}
                      type="email"
                      inputMode="email"
                      value={form.signer_email}
                      onChange={patch('signer_email')}
                      invalid={!!errors.signer_email}
                      autoComplete="email"
                    />
                  )}
                </Field>
                <Field
                  label="Phone"
                  error={errors.signer_phone}
                  className="col-span-2 sm:col-span-1"
                >
                  {(id, by) => (
                    <Input
                      id={id}
                      aria-describedby={by}
                      type="tel"
                      inputMode="tel"
                      value={form.signer_phone}
                      onChange={patch('signer_phone')}
                      invalid={!!errors.signer_phone}
                      autoComplete="tel"
                    />
                  )}
                </Field>
              </div>
            </div>
          </StepCard>

          <StepCard
            n={3}
            title="Agreement"
            description={`AskArthur Master Service Agreement (${TC_VERSION}). Review your Order and the legacy summary below. Separately signed agreements remain controlling.`}
            error={errors.agree}
            ref={
              !errors.plan && !formError && errors.agree
                ? firstErrorRef
                : undefined
            }
          >
            {pricing && (
              <IconFacts className="mb-4">
                <IconFact
                  icon={<IconCalendar size={15} />}
                  sub="month-to-month after"
                >
                  Six-month initial term
                </IconFact>
                <IconFact
                  icon={<IconCard size={15} />}
                  sub={
                    // A fee-only offer defers billing to its start date even
                    // when that date is near (or missing — then it's today).
                    startsLater || (feeOnly && startIso)
                      ? `from ${startDate}`
                      : feeOnly
                        ? 'set separately when the subscription starts'
                        : 'starts today'
                  }
                >
                  Monthly billing
                </IconFact>
                <IconFact
                  icon={<IconClock size={15} />}
                  sub={`after ${formatMinutes(pricing.includedMinutes)}`}
                >
                  {formatRate(pricing.overageCentsPerMinute, currency, 'min')}
                </IconFact>
                {pricing.perOrderCents > 0 && (
                  <IconFact
                    icon={<IconDollar size={15} />}
                    sub="per confirmed order"
                  >
                    {formatMoney(pricing.perOrderCents, currency)}
                  </IconFact>
                )}
              </IconFacts>
            )}
            <Reveal label="Read the full summary">
              <div
                className="v2-scroll max-h-[220px] overflow-y-auto rounded-v2 bg-v2-sunken px-4 py-3"
                tabIndex={0}
                aria-label="Agreement summary"
              >
                <p className="text-v2-12 font-semibold text-v2-fg-2">
                  AskArthur Master Service Agreement ({TC_VERSION}) — Summary
                </p>
                {TERMS_SUMMARY.map((t) => (
                  <p
                    key={t.slice(0, 24)}
                    className="mt-2 text-v2-12 leading-[18px] text-v2-fg-3"
                  >
                    {t}
                  </p>
                ))}
              </div>
            </Reveal>
            <div className="mt-4 border-t border-v2-line/70 pt-4">
              <Checkbox
                className="items-start [&>input]:mt-0.5"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                aria-invalid={!!errors.agree}
                label={`I, ${form.signer_name.trim() || 'the signer'}, am authorized to sign for ${form.business_name.trim() || 'this business'} and accept the AskArthur Service Agreement (${TC_VERSION}) and acknowledge the Privacy Policy.`}
              />
              <p className="mt-1.5 pl-6 text-v2-12 text-v2-fg-3">
                <Link
                  to="/legal/business/legacy-v3-summary"
                  target="_blank"
                  className="underline hover:text-v2-fg"
                >
                  Service Agreement
                </Link>{' '}
                ·{' '}
                <Link
                  to="/legal/privacy"
                  target="_blank"
                  className="underline hover:text-v2-fg"
                >
                  Privacy Policy
                </Link>
              </p>
            </div>
          </StepCard>
        </div>

        <Receipt
          sticky
          heading={
            <h2 className="text-v2-16 font-bold tracking-[-0.015em] text-v2-fg">
              Order summary
            </h2>
          }
          aria-label="Order summary"
        >
          {option && pricing ? (
            <>
              <div className="mb-3 flex items-center justify-between gap-3">
                <PlanName
                  code={option.code}
                  hue={option.hue ?? planHue(option.code)}
                  name={option.name}
                />
                {option.tag && (
                  <Tag tone={option.tag.tone}>{option.tag.label}</Tag>
                )}
              </div>
              <ReceiptGroup>
                <ReceiptLines lines={lines} />
              </ReceiptGroup>
              <ReceiptTotal
                className="mt-3"
                label="Monthly total"
                value={`${formatMoney(monthly, currency)}/mo`}
                note="+ tax"
              />
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-v2-12">
                <dt className="text-v2-fg-3">Included</dt>
                <dd className="v2-num text-right text-v2-fg-2">
                  {formatMinutes(pricing.includedMinutes)}
                  {groupScoped ? ' shared' : ''} / mo
                </dd>
                <dt className="text-v2-fg-3">After that</dt>
                <dd className="v2-num text-right text-v2-fg-2">
                  {formatRate(pricing.overageCentsPerMinute, currency, 'min')}
                </dd>
                {pricing.perOrderCents > 0 && (
                  <>
                    <dt className="text-v2-fg-3">Confirmed orders</dt>
                    <dd className="v2-num text-right text-v2-fg-2">
                      {formatRate(pricing.perOrderCents, currency, 'order')}
                    </dd>
                  </>
                )}
              </dl>
              <ReceiptDue
                className="mt-4"
                label="Due today"
                value={formatMoney(dueToday, currency, { exact: true })}
                note={
                  feeOnly
                    ? `${setup === 0 ? 'Payment-method setup only; no payment collected in this step.' : 'Setup fee only.'} Main subscription creation is separate${startIso ? `; the Order specifies ${startDate}` : '; its start date must be agreed separately'}. Recurring price: ${formatMoney(monthly, currency)}/mo.`
                    : startsLater
                      ? `${setup > 0 ? 'Setup fee now; ' : 'Setup fee waived. '}${formatMoney(monthly, currency)}/mo from ${startDate}, then monthly.`
                      : `${setup > 0 ? 'Setup fee plus the first month' : 'Setup fee waived; the first month is due now'}. Then ${formatMoney(monthly, currency)}/mo, renewing monthly.`
                }
              />
            </>
          ) : (
            <Reason>
              {multi
                ? 'Pick one of the offers to see the total.'
                : 'Pick a plan to see the total.'}
            </Reason>
          )}

          <div className="mt-4">
            {errorCount > 0 && (
              <Callout tone="danger" className="mb-3">
                {errorCount === 1
                  ? 'One field needs attention.'
                  : `${errorCount} fields need attention.`}
              </Callout>
            )}
            {serverError && (
              <Callout tone="danger" className="mb-3">
                {serverError}
              </Callout>
            )}
            <Button
              type="submit"
              variant="primary"
              className="h-10 w-full px-4 text-v2-14"
              loading={busy}
              trailing={<IconLock size={13} />}
              disabledReason={!option ? 'Pick a plan first.' : undefined}
            >
              Continue to secure payment
            </Button>
            <p className="mt-2 text-v2-12 leading-[18px] text-v2-fg-3">
              Card details are entered on Stripe. Nothing is charged until you
              confirm there.
            </p>
          </div>
          <NextSteps
            className="mt-5 border-t border-v2-line pt-4"
            title="What happens next?"
            items={[
              'Add your payment method on Stripe.',
              form.signer_email.trim()
                ? `Use ${form.signer_email.trim()} as the contact for this Order.`
                : 'Keep a copy of your Order and any separately signed agreement.',
              'Onboarding starts: we set up the phone line, menu and hours with you.',
            ]}
          />
        </Receipt>
      </form>
    </PublicShell>
  );
};

/** Group-scoped offer: locations × add-ons. Included add-ons show as a check. */
const LocationMatrix: React.FC<{
  locations: GroupLocation[];
  pricing: PlanPricing;
  currency: string;
  onToggle: (storeId: number, key: AddonKey, on: boolean) => void;
}> = ({ locations, pricing, currency, onToggle }) => (
  <div className="mt-4">
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h3 className="text-v2-13 font-semibold">Add-ons by location</h3>
      <span className="text-v2-12 text-v2-fg-3">
        {ADDON_KEYS.filter((k) => !pricing.addons[k].included)
          .map(
            (k) =>
              `${ADDON_META[k].label} ${formatRate(pricing.addons[k].cents, currency, 'mo')}`,
          )
          .join(' · ')}
      </span>
    </div>
    <div className="v2-scroll -mx-4 mt-2 overflow-x-auto sm:mx-0">
      <Table fixed className="min-w-[480px]">
        <THead>
          <tr>
            <TH>Location</TH>
            {ADDON_KEYS.map((k) => (
              <TH key={k} className="w-[128px]" align="center">
                {ADDON_META[k].short}
              </TH>
            ))}
            <TH className="w-[96px]" align="right">
              Add-ons
            </TH>
          </tr>
        </THead>
        <TBody>
          {locations.map((l) => {
            const cents = locationAddonCents(pricing, l);
            return (
              <TR key={l.store_id}>
                <TD className="font-medium text-v2-fg">{l.store_name}</TD>
                {ADDON_KEYS.map((k) => {
                  const included = pricing.addons[k].included;
                  return (
                    <TD key={k} align="center">
                      {included ? (
                        <span
                          className="inline-flex items-center gap-1 text-v2-12 text-v2-fg-3"
                          title="Included in the plan"
                        >
                          <IconCheckCircle size={13} /> Included
                        </span>
                      ) : (
                        <input
                          type="checkbox"
                          aria-label={`${ADDON_META[k].label} for ${l.store_name}`}
                          checked={l[LOCATION_ADDON_FIELD[k]]}
                          onChange={(e) =>
                            onToggle(l.store_id, k, e.target.checked)
                          }
                          className="h-4 w-4 rounded-v2-sm border-v2-line-strong text-v2-accent focus:ring-2 focus:ring-v2-accent/25 focus:ring-offset-0"
                        />
                      )}
                    </TD>
                  );
                })}
                <TD
                  num
                  align="right"
                  className={clsx(cents === 0 && 'text-v2-fg-3')}
                >
                  {cents === 0 ? '—' : `${formatMoney(cents, currency)}/mo`}
                </TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Dead ends and shells
// ---------------------------------------------------------------------------

const SecureNote: React.FC<{ label: string }> = ({ label }) => (
  <span className="inline-flex items-center gap-1">
    <IconLock size={11} /> {label}
  </span>
);

const DeadEnd: React.FC<{
  kind: 'paid' | 'expired' | 'canceled' | 'invalid';
  offer?: Offer;
  token?: string;
}> = ({ kind, offer, token }) => {
  const eyebrow = offer?.business_name
    ? `Offer for ${offer.business_name}`
    : undefined;
  const standard = (
    <Link to="/activate">
      <Button variant="secondary" className="h-10 px-4 text-v2-14">
        See standard plans
      </Button>
    </Link>
  );
  const copy = {
    paid: {
      title: 'You’re all set.',
      lede: `Payment went through${offer?.plan?.name ? ` and the ${offer.plan.name} plan is active` : ''}. Onboarding starts now — your AskArthur contact will reach out to set up the phone line, menu and hours.`,
      action: (
        <Link
          to={`/store-onboarding?activation=${encodeURIComponent(token ?? '')}`}
        >
          <PublicPrimary trailing={<IconArrowRight size={13} />}>
            Continue to onboarding
          </PublicPrimary>
        </Link>
      ),
      status: <StatusLabel tone="success">Paid</StatusLabel>,
    },
    expired: {
      title: 'This offer has expired.',
      lede: `${offer?.expiration_date ? `It was valid until ${formatOfferDate(offer.expiration_date)}. ` : ''}Nothing was charged. Your AskArthur contact can send a fresh link, or you can activate on a standard plan.`,
      action: standard,
      status: (
        <StatusLabel tone="neutral" hollow>
          Expired
        </StatusLabel>
      ),
    },
    canceled: {
      title: 'This offer was withdrawn.',
      lede: 'Nothing was charged. If you were expecting it, reply to the email it came in or write to us and we’ll sort it out.',
      action: (
        <a href={`mailto:${SUPPORT_EMAIL}`}>
          <Button variant="secondary" className="h-10 px-4 text-v2-14">
            Email support
          </Button>
        </a>
      ),
      status: <StatusLabel tone="neutral">Withdrawn</StatusLabel>,
    },
    invalid: {
      title: 'This link isn’t valid.',
      lede: 'It may have been copied incompletely. Check the link in your email, or activate on a standard plan.',
      action: standard,
      status: null,
    },
  }[kind];
  return (
    <PublicShell width="narrow" aside={<SecureNote label="Secure link" />}>
      <PageMeta title="AskArthur | Activate" description={copy.title} />
      {kind === 'paid' && (
        <IconCheckCircle size={28} className="mb-4 text-v2-success" />
      )}
      <div className="flex items-start justify-between gap-4">
        <PublicTitle
          eyebrow={kind === 'invalid' ? undefined : eyebrow}
          lede={copy.lede}
        >
          {copy.title}
        </PublicTitle>
        {copy.status && <div className="shrink-0 pt-6">{copy.status}</div>}
      </div>
      {kind === 'paid' && offer?.plan && (
        <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 border-t border-v2-line pt-4 text-v2-13">
          <dt className="text-v2-fg-3">Plan</dt>
          <dd>
            <PlanName code={offer.plan.code} name={offer.plan.name} size="sm" />
          </dd>
          <dt className="text-v2-fg-3">Monthly</dt>
          <dd className="v2-num">
            {formatMoney(offer.base_price_cents, offer.currency)}/mo ·{' '}
            {formatMinutes(offer.included_minutes)} included
          </dd>
          {offer.signer_email && (
            <>
              <dt className="text-v2-fg-3">Receipt</dt>
              <dd>Sent to {offer.signer_email}</dd>
            </>
          )}
        </dl>
      )}
      <div className="mt-7">{copy.action}</div>
    </PublicShell>
  );
};

const LoadingShell: React.FC = () => (
  <PublicShell width="checkout" aside={<SecureNote label="Secure checkout" />}>
    <div
      className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8"
      aria-busy
      aria-label="Loading"
    >
      <div>
        <Skeleton className="h-3 w-40" />
        <Skeleton className="mt-3 block h-6 w-72" />
        <Skeleton className="mt-3 block h-3 w-full max-w-[520px]" />
        <div className="mt-6 rounded-v2-xl border border-v2-line bg-v2-surface p-5 shadow-v2-card sm:p-6">
          <div className="flex items-center gap-3">
            <Skeleton className="h-6 w-6 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="mt-4 sm:pl-9">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="flex h-12 items-center gap-4 border-b border-v2-line/70 last:border-b-0"
              >
                <Skeleton className="h-2.5 w-2.5" />
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="ml-auto h-3 w-48" />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="self-start rounded-v2-xl border border-v2-line bg-v2-surface p-5 shadow-v2-card">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-4 block h-4 w-32" />
        <Skeleton className="mt-3 block h-3 w-full" />
        <Skeleton className="mt-2 block h-3 w-full" />
        <Skeleton className="mt-6 block h-10 w-full" />
      </div>
    </div>
  </PublicShell>
);
